/* Confirmation only: the product form owns cart requests and emits after a successful add. */
(() => {
  'use strict';
  const DURATION = 10000;

  function start() {
    const toast = document.querySelector('[data-cart-notification]');
    if (!toast || toast.dataset.initialized) return;
    toast.dataset.initialized = 'true';
    const list = toast.querySelector('[data-cart-notification-items]');
    const progress = toast.querySelector('[data-cart-notification-progress]');
    const countdown = toast.querySelector('[data-cart-notification-countdown]');
    const live = document.querySelector('[data-cart-notification-live]');
    if (!list || !progress || !countdown || !live) return;
    const pauses = new Set();
    let remaining = DURATION;
    let lastTime = 0;
    let frame = 0;
    let announcementTimer = 0;
    let trigger = null;

    function visibility(visible) {
      document.dispatchEvent(new CustomEvent('bulkpeptides:notification-visibility', { detail: { visible } }));
    }

    function paint() {
      progress.style.transform = `scaleX(${Math.max(0, remaining / DURATION)})`;
      const label = pauses.size ? 'Timer paused' : `Closes in ${Math.max(1, Math.ceil(remaining / 1000))}s`;
      if (countdown.textContent !== label) countdown.textContent = label;
    }

    function close(restoreFocus = true) {
      if (toast.hidden) return;
      const ownsFocus = toast.contains(document.activeElement);
      cancelAnimationFrame(frame);
      clearTimeout(announcementTimer);
      frame = 0;
      toast.hidden = true;
      pauses.clear();
      live.textContent = '';
      visibility(false);
      if (restoreFocus && ownsFocus && trigger?.isConnected && !trigger.disabled) trigger.focus({ preventScroll: true });
    }

    function tick(now) {
      frame = 0;
      if (toast.hidden || pauses.size) return;
      remaining = Math.max(0, remaining - (now - lastTime));
      lastTime = now;
      if (remaining === 0) { close(false); return; }
      paint();
      frame = requestAnimationFrame(tick);
    }

    function pause(reason, active) {
      if (toast.hidden) return;
      if (active) {
        if (!pauses.size && frame) remaining = Math.max(0, remaining - (performance.now() - lastTime));
        pauses.add(reason);
        cancelAnimationFrame(frame);
        frame = 0;
      } else {
        pauses.delete(reason);
        if (!pauses.size && !frame) { lastTime = performance.now(); frame = requestAnimationFrame(tick); }
      }
      paint();
    }

    function itemNode(item) {
      const row = document.createElement('li');
      row.className = 'cart-notification__item';
      let visual;
      if (typeof item.image === 'string' && item.image.trim()) {
        visual = document.createElement('img');
        visual.className = 'cart-notification__image';
        visual.alt = '';
        visual.width = 70;
        visual.height = 82;
        visual.src = item.image;
        visual.addEventListener('error', () => {
          const fallback = document.createElement('span');
          fallback.className = 'cart-notification__image cart-notification__image-placeholder';
          fallback.textContent = 'Image unavailable';
          fallback.setAttribute('aria-hidden', 'true');
          visual.replaceWith(fallback);
        }, { once: true });
      } else {
        visual = document.createElement('span');
        visual.className = 'cart-notification__image cart-notification__image-placeholder';
        visual.textContent = 'Added';
        visual.setAttribute('aria-hidden', 'true');
      }
      const copy = document.createElement('div');
      const name = document.createElement('p');
      name.className = 'cart-notification__name';
      name.textContent = item.title;
      copy.append(name);
      if (item.variantTitle && item.variantTitle !== 'Default Title') {
        const variant = document.createElement('p');
        variant.className = 'cart-notification__variant';
        variant.textContent = item.variantTitle;
        copy.append(variant);
      }
      const quantity = document.createElement('p');
      quantity.className = 'cart-notification__quantity';
      quantity.textContent = `Added quantity: ${item.quantity}`;
      copy.append(quantity);
      row.append(visual, copy);
      return row;
    }

    document.addEventListener('bulkpeptides:item-added', (event) => {
      const supplied = event.detail?.items;
      if (!Array.isArray(supplied)) return;
      const items = supplied.filter((item) => item && Number.isSafeInteger(Number(item.quantity)) && Number(item.quantity) > 0).map((item) => ({
        title: typeof item.title === 'string' && item.title.trim() ? item.title.trim() : 'Item',
        variantTitle: typeof item.variantTitle === 'string' ? item.variantTitle.trim() : '',
        image: item.image,
        quantity: Number(item.quantity)
      }));
      if (!items.length) return;
      cancelAnimationFrame(frame);
      clearTimeout(announcementTimer);
      frame = 0;
      remaining = DURATION;
      trigger = event.detail.trigger instanceof HTMLElement ? event.detail.trigger : null;
      list.replaceChildren(...items.map(itemNode));
      toast.hidden = false;
      pauses.clear();
      if (document.visibilityState === 'hidden') pauses.add('document');
      if (toast.contains(document.activeElement)) pauses.add('focus');
      if (window.matchMedia('(hover: hover)').matches && toast.matches(':hover')) pauses.add('hover');
      paint();
      visibility(true);
      live.textContent = '';
      announcementTimer = setTimeout(() => {
        live.textContent = `Added to your cart: ${items.map((item) => `${item.quantity} ${item.title}${item.variantTitle && item.variantTitle !== 'Default Title' ? `, ${item.variantTitle}` : ''}`).join('; ')}. View cart or continue shopping. This confirmation closes in 10 seconds and pauses while its controls are focused.`;
      }, 50);
      if (!pauses.size) { lastTime = performance.now(); frame = requestAnimationFrame(tick); }
    });

    toast.querySelector('[data-cart-notification-close]')?.addEventListener('click', () => close());
    toast.querySelector('[data-cart-notification-continue]')?.addEventListener('click', () => close());
    toast.querySelector('[data-cart-notification-view]')?.addEventListener('click', () => close(false));
    toast.addEventListener('pointerenter', (event) => { if (event.pointerType !== 'touch') pause('hover', true); });
    toast.addEventListener('pointerleave', (event) => { if (event.pointerType !== 'touch') pause('hover', false); });
    toast.addEventListener('focusin', () => pause('focus', true));
    toast.addEventListener('focusout', () => queueMicrotask(() => pause('focus', toast.contains(document.activeElement))));
    document.addEventListener('visibilitychange', () => pause('document', document.visibilityState === 'hidden'));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !toast.hidden) close(); });
    window.addEventListener('pagehide', () => close(false));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
